import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "next-themes";

// Public viewer pages and creator app are split into separate chunks to keep fan pages light.
const CreatorPage = lazy(() => import("@/public/CreatorPage"));
const RulePage = lazy(() => import("@/public/RulePage"));
const UnsubscribePage = lazy(() => import("@/public/UnsubscribePage"));
const LegalPage = lazy(() => import("@/public/LegalPage"));
const LegalDoc = lazy(() => import("@/legal/LegalDoc"));
const About = lazy(() => import("@/pages/About"));
const Vs = lazy(() => import("@/pages/Vs"));
const MyDataPage = lazy(() => import("@/public/MyDataPage"));
const Admin = lazy(() => import("@/pages/Admin"));
const PasswordReset = lazy(() => import("@/pages/PasswordReset"));
const CommentLinkPage = lazy(() => import("@/public/CommentLinkPage"));
const Family = lazy(() => import("@/pages/dashboard/Family"));
const Blog = lazy(() => import("@/blog/Blog"));
const BlogPost = lazy(() => import("@/blog/BlogPost"));
const Billing = lazy(() => import("@/pages/dashboard/Billing"));
const Pricing = lazy(() => import("@/public/Pricing"));
const Promote = lazy(() => import("@/pages/dashboard/Promote"));
const Automations = lazy(() => import("@/pages/dashboard/automations/Automations"));
const CreatorShell = lazy(() => import("@/pages/CreatorShell"));
const Home = lazy(() => import("@/pages/Home"));
const Login = lazy(() => import("@/pages/Login"));
const Signup = lazy(() => import("@/pages/Signup"));
import { AuthProvider } from "@/context/AuthContext";
import { LaunchGate } from "@/components/fanclaim/LaunchGate";
import { captureRef } from "@/lib/referral";
import { InstallPrompt } from "@/components/fanclaim/InstallPrompt";

captureRef();
const DashboardLayout = lazy(() => import("@/pages/DashboardLayout"));
const Overview = lazy(() => import("@/pages/dashboard/Insights"));
const Settings = lazy(() => import("@/pages/dashboard/Settings"));
const Rules = lazy(() => import("@/pages/dashboard/Rules"));
const Assets = lazy(() => import("@/pages/dashboard/Assets"));
const Subscribers = lazy(() => import("@/pages/dashboard/Subscribers"));

export default function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false} storageKey="famclaim-theme">
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
          <LaunchGate>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <Routes>
              <Route element={<CreatorShell />}>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<PasswordReset key="forgot" />} />
                <Route path="/reset-password" element={<PasswordReset key="reset" />} />
                <Route path="/dashboard" element={<DashboardLayout />}>
                  <Route index element={<Navigate to="overview" replace />} />
                  <Route path="overview" element={<Overview />} />
                  <Route path="rules" element={<Rules />} />
                  <Route path="assets" element={<Assets />} />
                  <Route path="subscribers" element={<Subscribers />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="admin" element={<Admin />} />
                  <Route path="automations" element={<Automations />} />
                  <Route path="promote" element={<Promote />} />
                  <Route path="billing" element={<Billing />} />
                  <Route path="family" element={<Family />} />
                  <Route path="*" element={<Navigate to="/dashboard/overview" replace />} />
                </Route>
              </Route>
              <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
              <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
              <Route path="/privacy-policy" element={<LegalDoc />} />
              <Route path="/fan-terms" element={<LegalDoc />} />
              <Route path="/dpa" element={<LegalDoc />} />
              <Route path="/referral-terms" element={<LegalPage />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/about" element={<About />} />
              <Route path="/vs/:slug" element={<Vs />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/terms" element={<LegalDoc />} />
              <Route path="/cookies" element={<LegalPage />} />
              <Route path="/my-data" element={<MyDataPage />} />
              <Route path="/c/:token" element={<CommentLinkPage />} />
              <Route path="/:username" element={<CreatorPage />} />
              <Route path="/:username/:slug" element={<RulePage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <InstallPrompt />
          </LaunchGate>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}
